import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/free-solid-svg-icons/faPrint";
import InitContext from "../pages/InitContext";
import DrugsTable from "../drugs/DrugsTable";
import useMmcService from "../../services/MmcService";
import Disclaimer from "../pages/Disclaimer";
import MedicalData from "../patient/MedicalData";
import Diseases from "../patient/Diseases";
import SymptomsTable from "../symptoms/SymptomsTable";
import LabTestsTable from "../labTests/LabTestsTable";
import MeasurementsTable from "../measurements/MeasurementsTable";
import {getDateStr, getPatientName} from "../pages/utils";
import PGx from "../pgx/PGx";
import { Consts } from "../pages/consts";
import {Tooltip} from "@mui/material";
import {faXmark} from "@fortawesome/free-solid-svg-icons";
import mmclogo from "../../resources/img/mmclogo2.png";

const ReportPrint = ({ selectedPatient, onFinishPrintReport }) => {
  const { t } = useTranslation();
  const context = useContext(InitContext);
  const { getDbDrugs, getDbUser } = useMmcService();
  const [patDrugs, setPatDrugs] = useState([]);
  const [patient, setPatient] = useState({});
  const [isPrinting, setIsPrinting] = useState(false);
  const patientId = selectedPatient ? selectedPatient?.id : context?.patient?.id;
  const isStudyDirector = context?.role === Consts.STUDY_DIRECTOR;
  const [selectedItems, setSelectedItems] = useState([]);
  const [showSelection, setShowSelection] = useState(false);

  const fetchData = async () => {
    const patient = selectedPatient
      ? selectedPatient
      : await getDbUser(context?.token, patientId);

    const drugsData = await getDbDrugs(context?.token, patientId, true, true);
    setPatient(patient);
    setPatDrugs(drugsData);
  };

  useEffect(() => {
    fetchData().then();
  }, [patientId]);

  useEffect(() => {
    if (selectedPatient) {
      setTimeout(async () => {
        await handlePrint();
      }, 5000);
    }
  }, [selectedPatient]);

  const dataToPrintRef = React.useRef();

  const handlePrint = async () => {
    setShowSelection(false);
    setIsPrinting(true);
    await fetchData();
    await generatePDF();
    setIsPrinting(false);

    if (onFinishPrintReport) {
      onFinishPrintReport();
    }

    //window.location.reload();
  };

  const generatePDF = async () => {
    // change viewport (not responsive) -> took solution setting imageWidth instead
    /*const vp = document?.getElementById("viewportMeta")?.getAttribute("content");
    document?.getElementById("viewportMeta")?.setAttribute("content", "width=800");*/
    const element = dataToPrintRef.current;

    if(element !== null){
      // set data to print visible
      element.style.visibility = "visible";
    }

    const canvas = await html2canvas(element, {
      windowWidth: "1200px",
    });

    element.style.visibility = "hidden";
    //document.getElementById("viewportMeta")?.setAttribute("content", vp);
    const pdf = new jsPDF();
    const imgWidth = 190;
    const pageHeight = pdf.internal.pageSize.height;
    const margin = 12;
    const usableHeight = pageHeight - 2 * margin;
    //const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let position = margin;
    let currentHeight = 0;
    let pageNumber = 1;

    while (currentHeight < canvas.height) {
      const pageCanvas = document.createElement("canvas");
      pageCanvas.width = canvas?.width;
      pageCanvas.height = (usableHeight * canvas?.width) / imgWidth;
      const canvasContext = pageCanvas?.getContext("2d");
      canvasContext.fillStyle = "#fff";
      canvasContext?.fillRect(0, 0, pageCanvas?.width, pageCanvas?.height);

      canvasContext?.drawImage(
        canvas,
        0,
        currentHeight,
        canvas?.width,
        pageCanvas?.height,
        0,
        0,
        pageCanvas.width,
        pageCanvas.height,
      );

      const pageImgData = pageCanvas?.toDataURL("image/jpeg");
      pdf?.addImage(pageImgData, "JPEG", 10, position, imgWidth, usableHeight);
      pdf?.setFontSize(8);
      const pageCenterX = pdf?.internal.pageSize.width / 2;
      const pageBottomY = pageHeight - 5; // 5 Punkte Abstand vom unteren Rand

      pdf.text(`${pageNumber}`, pageCenterX, pageBottomY, {
        align: "center",
      });

      currentHeight += pageCanvas?.height;
      pageNumber++;

      if (currentHeight < canvas?.height) {
        pdf?.addPage();
      }
    }

    const now = new Date();

    const timestamp = `${now.getFullYear()}${String(
      now.getMonth() + 1,
    )?.padStart(2, "0")}${String(now.getDate())?.padStart(2, "0")}${String(
      now.getHours(),
    )?.padStart(2, "0")}${String(now.getMinutes())?.padStart(2, "0")}`;

    const filename = `mmc-report-${getPatientName(
      selectedPatient || patient,
      isStudyDirector,
    )}_${timestamp}.pdf`;

    pdf?.save(filename);

    if (onFinishPrintReport) {
      onFinishPrintReport();
    }
  };

  const handleCheckboxChange = (e, item) => {
    if (e.target.checked) {
      setSelectedItems((prev) => [...prev, item.id]);
    } else {
      setSelectedItems((prev) =>
        prev.filter((id) => id !== item.id)
      );
    }
  }

  const printCategories = [
    { id: "diseases", name: t("diseases") },
    { id: "pgx", name: t("pgx_test") },
    { id: "drugs", name: t("ddi") },
    { id: "symptoms", name: t("symptoms") },
    { id: "labTests", name: t("lab_tests") },
    { id: "measurements", name: t("measurements") }
  ]

  return (
    <>
      {showSelection && (
        <div className="feedback-modal">
          <div className="print-modal-content">
            <header className="feedback-modal-header">
              <h2>{t("select")}</h2>
              <FontAwesomeIcon
                icon={faXmark}
                onClick={() => setShowSelection(false)}
              />
            </header>
            <div className="checkbox-group">
              {printCategories.map((item) => (
                <label key={item.id} className="checkbox-item">
                  <input
                    className="checkbox-item"
                    type="checkbox"
                    value={item.id}
                    onChange={(e) => handleCheckboxChange(e, item)}
                  />
                  {item.name}
                </label>
              ))}
            </div>
            <div className="feedback-modal-buttons">
              <button onClick={handlePrint} className="w3-btn w3-round-xlarge w3-theme-d5">
                {t("save")}
              </button>
              <button onClick={() => setShowSelection(false)} className="w3-btn w3-round-xlarge w3-theme-d5">
                {t("cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
      <div ref={dataToPrintRef} className="offscreen">
        <div className="print-centered w3-center">
          <img src={mmclogo} alt="MMC logo" className="mmc-logo" />
          <h1>
            {t("report_for")} {getPatientName(patient, isStudyDirector)}
            {" - "}
            {getDateStr(new Date(), true)}
          </h1>
          <br />
          <h2>{t("medical_data")}</h2>
          <MedicalData patient={patient} />
          { !selectedPatient && selectedItems?.includes("diseases") && <Diseases patient={ patient }/> }
          { selectedPatient && <Diseases patient={ patient }/> }
          <div className="page-break" />
          { !selectedPatient && selectedItems?.includes("pgx") && <h2>{ t("pharmacogenetic_test_results") }</h2> }
          { selectedPatient && <h2>{ t("pharmacogenetic_test_results") }</h2> }
          { !selectedPatient && selectedItems?.includes("pgx") &&
            <PGx init={false} printView={ true } onHandleBack={ null } onFinishInit={null} onHandleNext={null}
                 patientId={patient?.id} studyId={patient?.study_id}/>
          }
          { selectedPatient && <PGx init={false} printView={ true } onHandleBack={ null } onFinishInit={null}
                                    onHandleNext={null} patientId={selectedPatient?.id}
                                    studyId={selectedPatient?.study_id}/> }
          { !selectedPatient && selectedItems?.includes("pgx") && <div style={{breakAfter: "always"}} /> }
          { selectedPatient && <div style={{breakAfter: "always"}} /> }
          { !selectedPatient && selectedItems?.includes("drugs") && <h2>{ t("safety_information_for_medication") }</h2> }
          { selectedPatient && <h2>{ t("safety_risks_for_medication") }</h2> }
          {!selectedPatient && selectedItems?.includes("drugs") && <Disclaimer printView={true} />}
          { !selectedPatient && selectedItems?.includes("drugs") && <DrugsTable patDrugs={ patDrugs } printView={ true }/> }
          { selectedPatient && <DrugsTable patDrugs={ patDrugs } printView={ true }/> }
          { !selectedPatient && selectedItems?.includes("drugs") && <div style={{breakAfter: "always"}} /> }
          { selectedPatient && <div style={{breakAfter: "always"}} /> }
          { ((!selectedPatient && selectedItems?.includes("symptoms"))
            || (!selectedPatient && selectedItems?.includes("labTests"))
            || (!selectedPatient && selectedItems?.includes("measurements"))) && <h2>{ t("diary") }</h2> }
          { selectedPatient && <h2>{ t("diary") }</h2> }
          { !selectedPatient && selectedItems?.includes("symptoms") && <p>{ t("symptoms") }</p> }
          { selectedPatient && <p>{ t("symptoms") }</p> }
          { !selectedPatient && selectedItems?.includes("symptoms") && <SymptomsTable selectedPatient={ patient } printView={ true }/> }
          { selectedPatient && <SymptomsTable selectedPatient={ patient } printView={ true }/> }
          { !selectedPatient && selectedItems?.includes("symptoms") && <div style={{breakAfter: "always"}} /> }
          { selectedPatient && <div style={{breakAfter: "always"}} /> }
          { !selectedPatient && selectedItems?.includes("labTests") && <p>{ t("lab_tests") }</p> }
          { selectedPatient && <p>{ t("lab_tests") }</p> }
          { !selectedPatient && selectedItems?.includes("labTests") && <LabTestsTable selectedPatient={ patient } printView={ true }/> }
          { selectedPatient && <LabTestsTable selectedPatient={ patient } printView={ true }/> }
          { !selectedPatient && selectedItems?.includes("labTests") && <div style={{breakAfter: "always"}} /> }
          { selectedPatient&& <div style={{breakAfter: "always"}} /> }
          { !selectedPatient && selectedItems?.includes("measurements") && <p>{ t("measurements") }</p> }
          { selectedPatient && <p>{ t("measurements") }</p> }
          { !selectedPatient && selectedItems?.includes("measurements") && <MeasurementsTable selectedPatient={ patient } printView={ true }/> }
          { selectedPatient && <MeasurementsTable selectedPatient={ patient } printView={ true }/> }
        </div>
      </div>
      {!selectedPatient && (
        <Tooltip title={t("export")}
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                fontSize: '1.3rem',
              },
            },
          }}
          >
          <button
            className={
              selectedPatient
                ? "hidden"
                : "w3-bar-item w3-btn w3-round-xlarge w3-theme-d5"
            }
            onClick={() => setShowSelection(true)}
            disabled={isPrinting}
          >
            <FontAwesomeIcon icon={faPrint} />
          </button>
        </Tooltip>
      )}
    </>
  );
};

export default ReportPrint;
